import EditableDataTable from '../components/EditableDataTable';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as api from '../services/api';
import SharePointUpload from '../components/SharePointUpload';

export default function ComplianceTracker({ showToast, editMode }) {
  const [tab, setTab] = useState('overview');
  const [obligations, setObligations] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [scores, setScores] = useState({ overall: {}, byCategory: [] });
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [showObForm, setShowObForm] = useState(false);
  const [showIncForm, setShowIncForm] = useState(false);
  const [obForm, setObForm] = useState({ name:'', category:'regulatory', regulatory_body:'', frequency:'annual', due_date:'', department:'' });
  const [incForm, setIncForm] = useState({ description:'', category:'regulatory', date_identified:'', severity:'medium', root_cause:'', remediation_plan:'' });
  const [selectedOb, setSelectedOb] = useState(null);
  const [saving, setSaving] = useState(false);
  const pendingChanges = useRef({}); // { [obligationId]: { changedFields } }
  const originalObligations = useRef({}); // snapshot at edit-mode entry

  useEffect(() => { loadAll(); }, []);

  // Snapshot obligations when edit mode turns ON
  useEffect(() => {
    if (editMode) {
      originalObligations.current = {};
      obligations.forEach(o => { originalObligations.current[o.id] = { ...o }; });
      pendingChanges.current = {};
    } else {
      // Edit mode turned OFF — persist any pending changes
      const changes = pendingChanges.current;
      const ids = Object.keys(changes);
      if (ids.length === 0) return;
      setSaving(true);
      Promise.all(ids.map(id => api.updateObligation(id, changes[id])))
        .then(() => {
          showToast && showToast(`✅ ${ids.length} obligation${ids.length > 1 ? 's' : ''} saved`);
          pendingChanges.current = {};
          loadAll();
        })
        .catch(err => {
          showToast && showToast('❌ Save failed: ' + (err.response?.data?.error || err.message));
        })
        .finally(() => setSaving(false));
    }
  }, [editMode]);

  async function loadAll() {
    setLoading(true);
    try {
      const [obRes, incRes, polRes] = await Promise.all([
        api.getObligations(), api.getIncidents(), api.getPolicies()
      ]);
      setObligations(obRes.data || []);
      setIncidents(incRes.data || []);
      setPolicies(polRes.data || []);
    } catch (err) { showToast('Failed to load compliance data'); }
    finally { setLoading(false); }
    // Scores loaded separately so a view/query failure never blocks the register
    try {
      const scRes = await api.getComplianceScores();
      setScores(scRes.data || { overall:{}, byCategory:[] });
    } catch (_) {}
  }

  async function submitObligation() {
    try { await api.createObligation(obForm); showToast('Obligation added'); setShowObForm(false); loadAll(); }
    catch (err) { showToast('Failed to add obligation'); }
  }

  async function submitIncident() {
    try { await api.createIncident(incForm); showToast('Incident recorded'); setShowIncForm(false); loadAll(); }
    catch (err) { showToast('Failed to record incident'); }
  }

  async function updateObStatus(id, status) {
    try {
      const { data } = await api.updateObligation(id, { status });
      if (data._cycle_rolled && data._next_due_date) {
        const next = new Date(data._next_due_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'});
        showToast(`✅ Cycle closed — next due ${next}`);
      } else {
        showToast('Status updated');
      }
      loadAll();
    } catch (err) { showToast('Update failed'); }
  }

  // ── Completion history for the obligation open in the detail modal ──
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [backfill, setBackfill] = useState(null);      // dry-run result
  const [backfillBusy, setBackfillBusy] = useState(false);

  useEffect(() => {
    if (!selectedOb) { setHistory([]); return; }
    setHistoryLoading(true);
    api.getObligationHistory(selectedOb.id)
      .then(res => setHistory(res.data || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [selectedOb]);

  async function previewBackfill() {
    setBackfillBusy(true);
    try {
      const { data } = await api.backfillCycles(true);
      setBackfill(data);
      if (data.would_update === 0) showToast('✅ No stale due dates — everything is current');
    } catch (err) {
      showToast('Preview failed: ' + (err.response?.data?.error || err.message));
    } finally { setBackfillBusy(false); }
  }

  async function applyBackfill() {
    if (!window.confirm(`Roll ${backfill.would_update} obligation(s) forward to their next cycle? This is recorded in completion history.`)) return;
    setBackfillBusy(true);
    try {
      const { data } = await api.backfillCycles(false);
      showToast(`✅ ${data.updated} obligation(s) rolled forward`);
      setBackfill(null);
      loadAll();
    } catch (err) {
      showToast('Backfill failed: ' + (err.response?.data?.error || err.message));
    } finally { setBackfillBusy(false); }
  }

  // Mirrors backend computeNextDueDate() so the user sees the next cycle before clicking
  const FREQ_MONTHS = { monthly:1, quarterly:3, bi_annual:6, 'bi-annual':6, semi_annual:6, annual:12, yearly:12, biennial:24 };
  function nextDuePreview(ob) {
    const months = FREQ_MONTHS[String(ob.frequency||'').toLowerCase()];
    if (!months || !ob.due_date) return null;
    const addMonths = (date, m) => {
      const d = new Date(date);
      const day = d.getUTCDate();
      d.setUTCDate(1);
      d.setUTCMonth(d.getUTCMonth() + m);
      const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth()+1, 0)).getUTCDate();
      d.setUTCDate(Math.min(day, last));
      return d;
    };
    let next = addMonths(new Date(ob.due_date), months);
    const today = new Date(); today.setUTCHours(0,0,0,0);
    let guard = 0;
    while (next <= today && guard < 120) { next = addMonths(next, months); guard++; }
    return next.toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'});
  }

  const statusBadge = (s) => {
    const c = { compliant:'badge-green', due_soon:'badge-gold', overdue:'badge-red', breached:'badge-red' };
    return <span className={`badge ${c[s]||'badge-gray'}`}><span className="dot"></span> {s?.replace(/_/g,' ')}</span>;
  };

  const filteredObligations = obligations
    .filter(o => !catFilter  || o.category   === catFilter)
    .filter(o => !deptFilter || o.department === deptFilter);

  const TABS = [
    { id:'overview', label:'Overview' },
    { id:'register', label:'Obligation Register' },
    { id:'calendar', label:'Calendar' },
    { id:'incidents', label:'Incidents' },
    { id:'policies', label:'Policies & Permits' },
  ];

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div>
      <div className="section-header">
        <div className="section-title">🛡️ Compliance Tracker</div>
        <div style={{display:'flex', gap:8}}>
          <a className="topbar-btn btn-ghost" href={`${process.env.REACT_APP_API_URL || 'https://del-legal-api.azurewebsites.net/api'}/compliance/obligations/import/template`} download>📄 Download Template</a>
          <button className="topbar-btn btn-ghost" onClick={() => document.getElementById('compliance-import').click()}>📥 Import CSV / Excel</button>
          <input id="compliance-import" type="file" accept=".csv,.xlsx,.xls" style={{display:'none'}} onChange={async (e) => { const f = e.target.files[0]; if (!f) return; try { const { data } = await api.importObligations(f); showToast(data.message); loadAll(); } catch(err) { showToast('Import failed: ' + (err.response?.data?.error || err.message)); } e.target.value=''; }} />
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-bar">
        {TABS.map(t => <div key={t.id} className={`tab ${tab===t.id?'active':''}`} onClick={()=>setTab(t.id)}>{t.label}</div>)}
      </div>

      {/* OVERVIEW TAB */}
      {tab === 'overview' && (
        <div>
          <div className="stat-grid" style={{gridTemplateColumns:'repeat(3,1fr)'}}>
            <div className="stat-card">
              <div className="stat-icon green">✅</div>
              <div>
                <div className="stat-num">{scores.overall?.overall_score || 0}%</div>
                <div className="stat-label">Overall Compliance Score</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon gold">⏳</div>
              <div>
                <div className="stat-num">{obligations.filter(o=>o.status==='due_soon').length}</div>
                <div className="stat-label">Due Soon (within 30 days)</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon red">⚠️</div>
              <div>
                <div className="stat-num">{obligations.filter(o=>o.status==='overdue').length}</div>
                <div className="stat-label">Overdue Obligations</div>
              </div>
            </div>
          </div>
          {backfill && backfill.would_update > 0 && (
            <div className="card" style={{marginBottom:16,border:'1px solid #FDE68A',background:'#FFFBEB'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
                <div>
                  <div style={{fontWeight:700,color:'#92400E',fontSize:14}}>⚠️ {backfill.would_update} obligation(s) have stale due dates</div>
                  <div style={{fontSize:12,color:'#92400E',opacity:.85,marginTop:2}}>
                    These were completed before automatic cycle roll-forward existed. Review below, then apply.
                  </div>
                </div>
                <div style={{display:'flex',gap:8}}>
                  <button className="topbar-btn btn-ghost" onClick={()=>setBackfill(null)}>Cancel</button>
                  <button className="topbar-btn btn-primary" disabled={backfillBusy} onClick={applyBackfill}>
                    {backfillBusy ? 'Applying…' : `Apply to ${backfill.would_update}`}
                  </button>
                </div>
              </div>
              <div style={{maxHeight:260,overflowY:'auto',background:'#fff',borderRadius:8,border:'1px solid #FDE68A'}}>
                <table style={{fontSize:12,width:'100%'}}>
                  <thead><tr><th>Ref</th><th>Obligation</th><th>Frequency</th><th>Current Due</th><th></th><th>New Due</th><th>New Status</th></tr></thead>
                  <tbody>{backfill.changes.map(c => (
                    <tr key={c.id}>
                      <td style={{fontWeight:700,fontSize:11,color:'var(--mid)'}}>{c.ref}</td>
                      <td>{c.name}</td>
                      <td>{c.frequency}</td>
                      <td style={{color:'#DC2626',textDecoration:'line-through'}}>{new Date(c.old_due_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</td>
                      <td style={{color:'var(--muted)'}}>→</td>
                      <td style={{color:'#16A34A',fontWeight:600}}>{new Date(c.new_due_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</td>
                      <td>{statusBadge(c.new_status)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
              {backfill.skipped?.length > 0 && (
                <div style={{marginTop:10,fontSize:11,color:'#92400E'}}>
                  <strong>{backfill.skipped.length} skipped</strong> (no recurring frequency — one-off or contractual):{' '}
                  {backfill.skipped.map(s => s.ref).join(', ')}
                </div>
              )}
            </div>
          )}
          {saving && (
            <div style={{padding:'8px 16px',background:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:8,marginBottom:12,fontSize:13,color:'#1E40AF',display:'flex',alignItems:'center',gap:8}}>
              <span style={{display:'inline-block',width:14,height:14,border:'2px solid #3B82F6',borderTopColor:'transparent',borderRadius:'50%',animation:'spin 0.7s linear infinite'}}></span>
              Saving changes…
            </div>
          )}
          <div className="card">
            <div className="card-title">📊 Compliance Score by Category</div>
            <div style={{display:'flex', flexDirection:'column', gap:10, marginTop:4}}>
              {scores.byCategory?.map(c => (
                <div key={c.category}>
                  <div style={{display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4}}>
                    <span>{c.category?.replace(/_/g,' ')}</span><strong>{c.score_pct}%</strong>
                  </div>
                  <div className="pbar-wrap"><div className="pbar" style={{width:`${c.score_pct}%`, background: c.score_pct < 50 ? 'linear-gradient(90deg,#DC2626,#F87171)' : c.score_pct < 80 ? 'linear-gradient(90deg,#D97706,#FCD34D)' : undefined}}></div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* REGISTER TAB */}
      {tab === 'register' && (
        <div>
          <div style={{display:'flex', gap:8, marginBottom:16, flexWrap:'wrap'}}>
            <select value={catFilter} onChange={e=>setCatFilter(e.target.value)} style={{fontSize:12, padding:'6px 10px', borderRadius:8, border:'1px solid var(--border)'}}>
              <option value="">All Categories</option>
              <option value="regulatory">Regulatory</option>
              <option value="tax_statutory">Tax & Statutory</option>
              <option value="labour_hr">Labour & HR</option>
              <option value="hse_permits">HSE/Permits</option>
              <option value="contractual">Contractual</option>
            </select>
            <select value={deptFilter} onChange={e=>setDeptFilter(e.target.value)} style={{fontSize:12, padding:'6px 10px', borderRadius:8, border:'1px solid var(--border)'}}>
              <option value="">All Departments</option>
              <option>Finance</option><option>Operations</option><option>HSE</option>
              <option>Human Resources</option><option>ICT</option><option>Legal</option>
              <option>Procurement</option><option>Projects</option><option>Commercial</option>
            </select>
            <button className="topbar-btn btn-primary" onClick={()=>setShowObForm(!showObForm)}>+ Add Obligation</button>
            <button className="topbar-btn btn-ghost" disabled={backfillBusy} onClick={previewBackfill} title="Find obligations whose due date never advanced and roll them to the current cycle">
              {backfillBusy ? 'Checking…' : '🔄 Check Stale Due Dates'}
            </button>
          </div>
          {showObForm && (
            <div className="card" style={{marginBottom:16}}>
              <div className="form-grid">
                <div className="form-group full"><label>Obligation Name</label><input type="text" value={obForm.name} onChange={e=>setObForm({...obForm,name:e.target.value})} /></div>
                <div className="form-group"><label>Category</label><select value={obForm.category} onChange={e=>setObForm({...obForm,category:e.target.value})}><option value="regulatory">Regulatory</option><option value="tax_statutory">Tax & Statutory</option><option value="labour_hr">Labour & HR</option><option value="hse_permits">HSE/Permits</option><option value="contractual">Contractual</option></select></div>
                <div className="form-group"><label>Regulatory Body</label><input type="text" value={obForm.regulatory_body} onChange={e=>setObForm({...obForm,regulatory_body:e.target.value})} /></div>
                <div className="form-group"><label>Frequency</label><select value={obForm.frequency} onChange={e=>setObForm({...obForm,frequency:e.target.value})}><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="annual">Annual</option><option value="bi_annual">Bi-Annual</option></select></div>
                <div className="form-group"><label>Due Date</label><input type="date" value={obForm.due_date} onChange={e=>setObForm({...obForm,due_date:e.target.value})} /></div>
                <div className="form-group"><label>Department</label><select value={obForm.department} onChange={e=>setObForm({...obForm,department:e.target.value})}><option value="">-- Select Department --</option><option>Finance</option><option>Operations</option><option>HSE</option><option>Human Resources</option><option>ICT</option><option>Legal</option><option>Procurement</option><option>Projects</option><option>Commercial</option></select></div>
              </div>
              <div className="form-actions"><button className="topbar-btn btn-ghost" onClick={()=>setShowObForm(false)}>Cancel</button><button className="topbar-btn btn-primary" onClick={submitObligation}>Add</button></div>
            </div>
          )}
          <div className="card">
            {editMode ? (
              <EditableDataTable
                columns={[{key:'ref',label:'Ref'},{key:'name',label:'Obligation'},{key:'category',label:'Category'},{key:'department',label:'Department'},{key:'regulatory_body',label:'Body'},{key:'frequency',label:'Frequency'},{key:'due_date',label:'Due Date'},{key:'status',label:'Status'}]}
                data={filteredObligations}
                editable={true}
                onDataChange={(updated) => {
                  setObligations(updated);
                  // Track which fields changed per obligation
                  updated.forEach(row => {
                    const orig = originalObligations.current[row.id];
                    if (!orig) return;
                    const diff = {};
                    ['name','category','department','regulatory_body','frequency','due_date','status'].forEach(k => {
                      if (String(row[k] ?? '') !== String(orig[k] ?? '')) diff[k] = row[k];
                    });
                    if (Object.keys(diff).length > 0) pendingChanges.current[row.id] = diff;
                    else delete pendingChanges.current[row.id];
                  });
                }}
                renderCell={(row, colKey) => {
                  if (colKey === 'ref') return <span onClick={()=>setSelectedOb(row)} style={{fontWeight:700,color:'var(--mid)',fontSize:11,cursor:'pointer',textDecoration:'underline dotted',textUnderlineOffset:3}}>{row.ref}</span>;
                  if (colKey === 'category') return row.category?.replace(/_/g,' ');
                  if (colKey === 'due_date') return row.due_date ? new Date(row.due_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '-';
                  if (colKey === 'status') return statusBadge(row.status);
                  return row[colKey] ?? '-';
                }}
              />
            ) : (
            <table><thead><tr><th>Ref</th><th>Obligation</th><th>Category</th><th>Department</th><th>Body</th><th>Frequency</th><th>Due Date</th><th>Status</th><th>Action</th></tr></thead>
            <tbody>{filteredObligations.map(o => (
              <tr key={o.id}>
                <td><span onClick={()=>setSelectedOb(o)} style={{fontWeight:700,color:'var(--mid)',fontSize:11,cursor:'pointer',textDecoration:'underline dotted',textUnderlineOffset:3}}>{o.ref}</span></td>
                <td>{o.name}</td>
                <td>{o.category?.replace(/_/g,' ')}</td>
                <td>{o.department||'-'}</td>
                <td>{o.regulatory_body||'-'}</td>
                <td>{o.frequency}</td>
                <td>{o.due_date ? new Date(o.due_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '-'}</td>
                <td>{statusBadge(o.status)}</td>
                <td>{o.status!=='compliant' && <button className="sig-btn primary" onClick={()=>updateObStatus(o.id,'compliant')}>Mark Compliant</button>}</td>
              </tr>
            ))}</tbody></table>
            )}
          </div>
          <SharePointUpload linkedEntityType="compliance" showToast={showToast} />
        </div>
      )}

      {/* CALENDAR TAB */}
      {tab === 'calendar' && (
        <div className="card">
          <div className="card-title">📅 Compliance Calendar</div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8}}>
            {obligations.filter(o=>o.due_date).sort((a,b)=>new Date(a.due_date)-new Date(b.due_date)).map(o => {
              const d = Math.ceil((new Date(o.due_date)-new Date())/(1000*60*60*24));
              const bg = d<0?'#FEF2F2':d<=30?'#FFFBEB':'#ECFDF5';
              const border = d<0?'#FECACA':d<=30?'#FDE68A':'#A7F3D0';
              return (
                <div key={o.id} style={{padding:12, background:bg, border:`1px solid ${border}`, borderRadius:8}}>
                  <div style={{fontSize:11, fontWeight:600, color:'var(--navy)'}}>{o.name}</div>
                  <div style={{fontSize:10, color:'var(--muted)', marginTop:4}}>{new Date(o.due_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</div>
                  <div style={{fontSize:10, marginTop:4}}>{statusBadge(o.status)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* INCIDENTS TAB */}
      {tab === 'incidents' && (
        <div>
          <button className="topbar-btn btn-primary" style={{marginBottom:16}} onClick={()=>setShowIncForm(!showIncForm)}>+ Report Incident</button>
          {showIncForm && (
            <div className="card" style={{marginBottom:16}}>
              <div className="form-grid">
                <div className="form-group full"><label>Description</label><textarea value={incForm.description} onChange={e=>setIncForm({...incForm,description:e.target.value})} /></div>
                <div className="form-group"><label>Category</label><select value={incForm.category} onChange={e=>setIncForm({...incForm,category:e.target.value})}><option value="regulatory">Regulatory</option><option value="tax_statutory">Tax & Statutory</option><option value="labour_hr">Labour & HR</option><option value="hse_permits">HSE/Permits</option><option value="contractual">Contractual</option></select></div>
                <div className="form-group"><label>Date Identified</label><input type="date" value={incForm.date_identified} onChange={e=>setIncForm({...incForm,date_identified:e.target.value})} /></div>
                <div className="form-group"><label>Severity</label><select value={incForm.severity} onChange={e=>setIncForm({...incForm,severity:e.target.value})}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option></select></div>
                <div className="form-group full"><label>Root Cause</label><input type="text" value={incForm.root_cause} onChange={e=>setIncForm({...incForm,root_cause:e.target.value})} /></div>
                <div className="form-group full"><label>Remediation Plan</label><textarea value={incForm.remediation_plan} onChange={e=>setIncForm({...incForm,remediation_plan:e.target.value})} /></div>
              </div>
              <div className="form-actions"><button className="topbar-btn btn-ghost" onClick={()=>setShowIncForm(false)}>Cancel</button><button className="topbar-btn btn-primary" onClick={submitIncident}>Submit</button></div>
            </div>
          )}
          <div className="card">
            <table><thead><tr><th>Ref</th><th>Description</th><th>Category</th><th>Date</th><th>Severity</th><th>Status</th></tr></thead>
            <tbody>{incidents.map(i => (
              <tr key={i.id}>
                <td style={{fontWeight:600,color:'var(--mid)',fontSize:11}}>{i.ref}</td>
                <td>{i.description?.slice(0,60)}</td>
                <td>{i.category?.replace(/_/g,' ')}</td>
                <td>{i.date_identified ? new Date(i.date_identified).toLocaleDateString('en-GB') : '-'}</td>
                <td><span className={`badge ${i.severity==='critical'||i.severity==='high'?'badge-red':'badge-gold'}`}>{i.severity}</span></td>
                <td><span className={`badge ${i.status==='resolved'||i.status==='closed'?'badge-green':'badge-gold'}`}>{i.status?.replace(/_/g,' ')}</span></td>
              </tr>
            ))}</tbody></table>
          </div>
        </div>
      )}

      {/* POLICIES TAB */}
      {tab === 'policies' && (
        <div>
          <div className="card">
            <div className="card-title">📜 Policies, Permits & Licences</div>
            <table><thead><tr><th>Name</th><th>Type</th><th>Issuing Body</th><th>Version</th><th>Issue Date</th><th>Expiry</th><th>Status</th></tr></thead>
            <tbody>{policies.map(p => (
              <tr key={p.id}>
                <td>{p.name}</td><td>{p.type}</td><td>{p.issuing_body||'-'}</td><td>{p.version||'-'}</td>
                <td>{p.issue_date ? new Date(p.issue_date).toLocaleDateString('en-GB') : '-'}</td>
                <td>{p.expiry_date ? new Date(p.expiry_date).toLocaleDateString('en-GB') : '-'}</td>
                <td><span className={`badge ${p.status==='active'?'badge-green':'badge-red'}`}>{p.status}</span></td>
              </tr>
            ))}</tbody></table>
          </div>
          <SharePointUpload linkedEntityType="policy" showToast={showToast} />
        </div>
      )}

      {/* ── OBLIGATION DETAIL MODAL ── */}
      {selectedOb && (
        <div onClick={()=>setSelectedOb(null)} style={{position:'fixed',inset:0,background:'rgba(10,20,50,0.72)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(3px)',WebkitBackdropFilter:'blur(3px)'}}>
          <div onClick={e=>e.stopPropagation()} style={{background:'#ffffff',borderRadius:12,padding:28,width:560,maxWidth:'94vw',maxHeight:'85vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.5)',border:'1px solid #e2e8f0'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20}}>
              <div>
                <span style={{fontWeight:800,fontSize:13,color:'var(--mid)',letterSpacing:1}}>{selectedOb.ref}</span>
                <div style={{fontWeight:700,fontSize:17,color:'var(--navy)',marginTop:2}}>{selectedOb.name}</div>
              </div>
              <button onClick={()=>setSelectedOb(null)} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',color:'var(--muted)',lineHeight:1}}>✕</button>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px 20px',fontSize:13}}>
              {[
                ['Category', selectedOb.category?.replace(/_/g,' ')],
                ['Department', selectedOb.department || '—'],
                ['Regulatory Body', selectedOb.regulatory_body || '—'],
                ['Frequency', selectedOb.frequency],
                ['Due Date', selectedOb.due_date ? new Date(selectedOb.due_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '—'],
                ['Status', selectedOb.status],
                ['Responsible Officer', selectedOb.responsible_officer_name || selectedOb.officer_name || '—'],
                ['Last Completed', selectedOb.last_completed_at ? new Date(selectedOb.last_completed_at).toLocaleDateString('en-GB') : '—'],
              ].map(([label, val]) => (
                <div key={label}>
                  <div style={{fontSize:10,fontWeight:600,color:'var(--muted)',textTransform:'uppercase',letterSpacing:0.8,marginBottom:2}}>{label}</div>
                  <div style={{fontWeight:500,color:'var(--navy)'}}>{val}</div>
                </div>
              ))}
            </div>
            {selectedOb.description && (
              <div style={{marginTop:14}}>
                <div style={{fontSize:10,fontWeight:600,color:'var(--muted)',textTransform:'uppercase',letterSpacing:0.8,marginBottom:4}}>Description</div>
                <div style={{fontSize:13,color:'var(--navy)',lineHeight:1.5}}>{selectedOb.description}</div>
              </div>
            )}
            <div style={{marginTop:16,padding:'10px 12px',background:'var(--bg)',borderRadius:7,fontFamily:'monospace',fontSize:11,color:'var(--muted)',display:'flex',alignItems:'center',gap:8}}>
              <span style={{fontWeight:600,color:'var(--navy)'}}>ID:</span>
              <span style={{flex:1,wordBreak:'break-all'}}>{selectedOb.id}</span>
              <button onClick={()=>{navigator.clipboard.writeText(selectedOb.id);showToast&&showToast('UUID copied!');}} style={{background:'var(--mid)',color:'#fff',border:'none',borderRadius:5,padding:'3px 10px',fontSize:11,cursor:'pointer',whiteSpace:'nowrap'}}>Copy</button>
            </div>
            {/* ── COMPLETION HISTORY ── */}
            <div style={{marginTop:18,borderTop:'1px solid #e2e8f0',paddingTop:14}}>
              <div style={{fontSize:10,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:0.8,marginBottom:8}}>
                Completion History
              </div>
              {historyLoading ? (
                <div style={{fontSize:12,color:'var(--muted)'}}>Loading…</div>
              ) : history.length === 0 ? (
                <div style={{fontSize:12,color:'var(--muted)',fontStyle:'italic'}}>No completed cycles yet.</div>
              ) : (
                <div style={{maxHeight:150,overflowY:'auto'}}>
                  {history.map(h => (
                    <div key={h.id} style={{display:'flex',alignItems:'center',gap:10,padding:'6px 0',borderBottom:'1px solid #f1f5f9',fontSize:12}}>
                      <span style={{color:'#16A34A',fontSize:13}}>✓</span>
                      <div style={{flex:1}}>
                        <div style={{color:'var(--navy)',fontWeight:500}}>
                          Cycle due {h.cycle_due_date ? new Date(h.cycle_due_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '—'}
                          {h.next_due_date && <span style={{color:'var(--muted)',fontWeight:400}}> → next {new Date(h.next_due_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</span>}
                        </div>
                        <div style={{fontSize:10,color:'var(--muted)',marginTop:1}}>
                          Completed {new Date(h.completed_at).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}
                          {h.completed_by_name ? ` by ${h.completed_by_name}` : ''}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{marginTop:16,display:'flex',gap:10,justifyContent:'flex-end',alignItems:'center'}}>
              {selectedOb.status !== 'compliant' && nextDuePreview(selectedOb) && (
                <span style={{fontSize:11,color:'var(--muted)',marginRight:'auto'}}>
                  Next cycle will be due <strong style={{color:'var(--navy)'}}>{nextDuePreview(selectedOb)}</strong>
                </span>
              )}
              {selectedOb.status !== 'compliant' && (
                <button className="topbar-btn btn-primary" onClick={()=>{updateObStatus(selectedOb.id,'compliant');setSelectedOb(null);}}>✓ Mark Compliant</button>
              )}
              <button className="topbar-btn btn-ghost" onClick={()=>setSelectedOb(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
